import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X } from 'lucide-react';
import { homeworkService } from '../services/api';
import { Button, Input } from './ui';

const HOMEWORK_CATEGORIES = {
  mindfulness: {
    title: 'Mindfulness & Awareness',
    description: 'Foundational mindfulness practice for anxiety, stress, and emotional regulation',
    phase: 'Early Wellness',
    color: 'bg-blue-50 border-blue-200 text-blue-900'
  },
  advanced: {
    title: 'Advanced Mental Wellness Companion Program',
    description: 'Comprehensive CBT, mindfulness, and behavior change system',
    phase: 'Mid Wellness',
    color: 'bg-purple-50 border-purple-200 text-purple-900'
  },
  insurance: {
    title: 'Insurance-Justified Templates',
    description: 'Medical necessity documentation with clinical compliance focus',
    phase: 'Established Wellness',
    color: 'bg-green-50 border-green-200 text-green-900'
  },
  coaching: {
    title: 'Coaching Tools & Workbook',
    description: 'Coaching worksheets and workbook resources focused on growth and performance',
    phase: 'Coaching Track',
    color: 'bg-amber-50 border-amber-200 text-amber-900'
  }
};

function HomeworkAssignmentModal({
  isOpen,
  onClose,
  clients,
  onAssignSuccess,
  allowedSetIds = null,
  modalTitle = 'Assign Homework'
}) {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [dueDate, setDueDate] = useState('');
  const [clinicianNote, setClinicianNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [manageClientId, setManageClientId] = useState('');
  const [manageStatus, setManageStatus] = useState('all');
  const [managedAssignments, setManagedAssignments] = useState([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageActionLoading, setManageActionLoading] = useState(null);

  const allowedCategories = useMemo(() => {
    return Object.entries(HOMEWORK_CATEGORIES).filter(([key]) => {
      if (!allowedSetIds || allowedSetIds.length === 0) return true;
      return allowedSetIds.includes(key);
    });
  }, [allowedSetIds]);

  useEffect(() => {
    if (!selectedCategory) return;
    if (!allowedCategories.some(([key]) => key === selectedCategory)) {
      setSelectedCategory('');
      setSelectedTemplateId('');
    }
  }, [allowedCategories, selectedCategory]);

  const loadTemplates = useCallback(async () => {
    try {
      const singleSetFilter = allowedSetIds?.length === 1 ? allowedSetIds[0] : null;
      const response = await homeworkService.getTemplates(singleSetFilter);
      let templateData = response.data;

      if (allowedSetIds && allowedSetIds.length > 0) {
        templateData = templateData.filter((template) => allowedSetIds.includes(template.setId));
      }

      setTemplates(templateData);
    } catch (error) {
      console.error('Failed to load templates:', error);
      setMessage({ type: 'error', text: 'Failed to load homework templates' });
    }
  }, [allowedSetIds]);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen, loadTemplates]);

  useEffect(() => {
    if (!isOpen) return;
    const firstClientId = clients?.[0]?._id || '';
    setManageClientId((prev) => prev || firstClientId);
  }, [isOpen, clients]);

  const loadManagedAssignments = useCallback(async () => {
    if (!manageClientId) {
      setManagedAssignments([]);
      return;
    }

    setManageLoading(true);
    try {
      const response = await homeworkService.getClientHomework(manageClientId, manageStatus);
      setManagedAssignments(response.data?.data || []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to load assignments for management.'
      });
    } finally {
      setManageLoading(false);
    }
  }, [manageClientId, manageStatus]);

  useEffect(() => {
    if (!isOpen) return;
    loadManagedAssignments();
  }, [isOpen, loadManagedAssignments]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedTemplateId('');
  };

  const getTemplatesForCategory = () => {
    if (!selectedCategory) return [];
    const categoryTemplates = templates.filter(t => t.setId === selectedCategory);
    return categoryTemplates;
  };


  const handleClientToggle = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAllClients = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(c => c._id));
    }
  };

  const handleAssign = async () => {
    setMessage(null);

    if (!selectedCategory) {
      setMessage({ type: 'error', text: 'Please select a homework category' });
      return;
    }

    if (!selectedTemplateId) {
      setMessage({ type: 'error', text: 'Please select a homework worksheet' });
      return;
    }

    if (selectedClients.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one client' });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        templateId: selectedTemplateId,
        clientIds: selectedClients,
        dueDate: dueDate || null,
        clinicianNote: clinicianNote.trim()
      };

      const response = await homeworkService.assignHomework(payload);
      const summary = response?.data?.data?.deliverySummary;
      const failedCount = summary?.failedEmailCount || 0;
      const emailedCount = summary?.emailedCount || 0;

      if (failedCount > 0) {
        setMessage({
          type: 'warning',
          text: `Homework assigned to ${selectedClients.length} client(s). ${emailedCount} email(s) sent, ${failedCount} failed. Use Manage Assignments > Resend after correcting email settings.`
        });
      } else {
        setMessage({
          type: 'success',
          text: `Homework assigned to ${selectedClients.length} client(s). ${emailedCount} email(s) delivered.`
        });
      }

      loadManagedAssignments();

      // Reset form
      if (failedCount === 0) {
        setTimeout(() => {
          setSelectedCategory('');
          setSelectedTemplateId('');
          setSelectedClients([]);
          setDueDate('');
          setClinicianNote('');
          setMessage(null);

          if (onAssignSuccess) {
            onAssignSuccess();
          }

          onClose();
        }, 2000);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to assign homework' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const templatesForCategory = getTemplatesForCategory();
  const sortedTemplates = [...templatesForCategory].sort((a, b) => {
    const typeA = a.type || `${a.setId} - ${a.title}`;
    const typeB = b.type || `${b.setId} - ${b.title}`;
    return typeA.localeCompare(typeB);
  });

  const handleManageAction = async (assignmentId, action) => {
    setManageActionLoading(`${action}:${assignmentId}`);
    try {
      if (action === 'archive') {
        await homeworkService.archiveAssignment(assignmentId);
      } else if (action === 'delete') {
        await homeworkService.deleteAssignment(assignmentId);
      } else if (action === 'refresh') {
        await homeworkService.refreshAssignment(assignmentId);
      } else if (action === 'alert') {
        await homeworkService.alertAssignment(assignmentId);
      } else if (action === 'resend') {
        await homeworkService.resendAssignment(assignmentId);
      }

      setMessage({ type: 'success', text: `Homework ${action} action completed.` });
      await loadManagedAssignments();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || `Failed to ${action} homework assignment.`
      });
    } finally {
      setManageActionLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-neutral-900">{modalTitle}</h2>
          <button
            onClick={onClose}
            className="p-2 text-neutral-500 hover:text-neutral-700 rounded-lg hover:bg-neutral-100"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Status Message */}
          {message && (
            <div className={`mb-6 p-4 rounded-lg ${
              message.type === 'error' 
                ? 'bg-red-50 text-red-700 border border-red-200' 
                : message.type === 'warning'
                  ? 'bg-amber-50 text-amber-800 border border-amber-200'
                  : 'bg-green-50 text-green-700 border border-green-200'
            }`}>
              {message.text}
            </div>
          )}

          {/* Step 1: Select Category */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-neutral-800 mb-3">
              1. Select Homework Category
            </h3>
            <div className="grid gap-3">
              {allowedCategories.map(([key, category]) => (
                <button
                  key={key}
                  onClick={() => handleCategoryChange(key)}
                  className={`text-left p-4 rounded-lg border-2 transition-all ${
                    selectedCategory === key
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-neutral-900">
                          {category.title}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${category.color}`}>
                          {category.phase}
                        </span>
                      </div>
                      <p className="text-sm text-neutral-600">{category.description}</p>
                    </div>
                    {selectedCategory === key && (
                      <svg className="w-6 h-6 text-primary-500 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Select Homework Type */}
          {selectedCategory && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-neutral-800 mb-3">
                2. Select Homework Type
              </h3>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Choose a homework type...</option>
                {sortedTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.type || `${template.setId} - ${template.title}`}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-neutral-500">
                {templatesForCategory.length} worksheet{templatesForCategory.length !== 1 ? 's' : ''} available in this category.
              </p>
            </div>
          )}

          {/* Step 3: Select Clients */}
          {selectedTemplateId && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-neutral-800">
                  3. Select Clients
                </h3>
                <button
                  onClick={handleSelectAllClients}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  {selectedClients.length === clients.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="border border-neutral-200 rounded-lg max-h-60 overflow-y-auto">
                {clients.map((client) => (
                  <label
                    key={client._id}
                    className="flex items-center gap-3 p-3 hover:bg-neutral-50 cursor-pointer border-b border-neutral-100 last:border-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedClients.includes(client._id)}
                      onChange={() => handleClientToggle(client._id)}
                      className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-neutral-900">
                        {client.firstName} {client.lastName}
                      </div>
                      <div className="text-xs text-neutral-500">
                        {client.email} • Day {client.currentDay || 1}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="mt-2 text-sm text-neutral-600">
                {selectedClients.length} client(s) selected
              </div>
            </div>
          )}

          {/* Step 4: Optional Details */}
          {selectedClients.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-neutral-800 mb-3">
                4. Additional Details (Optional)
              </h3>
              <div className="space-y-4">
                <Input
                  label="Due Date (Optional)"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  fullWidth
                />
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Note to Client (Optional)
                  </label>
                  <textarea
                    value={clinicianNote}
                    onChange={(e) => setClinicianNote(e.target.value)}
                    placeholder="Add any specific instructions or context for this assignment..."
                    rows={3}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="mb-6 border border-neutral-200 rounded-lg p-4 bg-neutral-50">
            <h3 className="text-lg font-semibold text-neutral-800 mb-3">
              5. Manage Existing Assignments
            </h3>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Client
                </label>
                <select
                  value={manageClientId}
                  onChange={(e) => setManageClientId(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select client...</option>
                  {clients.map((client) => (
                    <option key={`manage-${client._id}`} value={client._id}>
                      {client.firstName} {client.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Status filter
                </label>
                <select
                  value={manageStatus}
                  onChange={(e) => setManageStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
            </div>

            <div className="mt-3 flex justify-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={loadManagedAssignments}
                disabled={!manageClientId || manageLoading}
              >
                {manageLoading ? 'Refreshing...' : 'Refresh list'}
              </Button>
            </div>

            <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
              {manageLoading && (
                <p className="text-sm text-neutral-500">Loading assignments...</p>
              )}
              {!manageLoading && managedAssignments.length === 0 && (
                <p className="text-sm text-neutral-500">No assignments found for selected client/filter.</p>
              )}
              {!manageLoading && managedAssignments.map((assignment) => (
                <div key={`assignment-manage-${assignment._id}`} className="rounded-lg border border-neutral-200 bg-white p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-neutral-900">{assignment.title}</p>
                      <p className="text-xs text-neutral-500">
                        Assigned: {assignment.assignedAt ? new Date(assignment.assignedAt).toLocaleDateString() : 'N/A'} • Status: {assignment.completionStatus || (assignment.isActive ? 'active' : 'archived')}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleManageAction(assignment._id, 'refresh')}
                        disabled={manageActionLoading === `refresh:${assignment._id}`}
                      >
                        Refresh
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleManageAction(assignment._id, 'alert')}
                        disabled={manageActionLoading === `alert:${assignment._id}`}
                      >
                        Alert
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleManageAction(assignment._id, 'resend')}
                        disabled={manageActionLoading === `resend:${assignment._id}`}
                      >
                        Resend
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleManageAction(assignment._id, 'archive')}
                        disabled={manageActionLoading === `archive:${assignment._id}`}
                      >
                        Archive
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (window.confirm('Delete this homework assignment?')) {
                            handleManageAction(assignment._id, 'delete');
                          }
                        }}
                        disabled={manageActionLoading === `delete:${assignment._id}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-200">
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={loading || !selectedCategory || !selectedTemplateId || selectedClients.length === 0}
            >
              {loading ? 'Assigning...' : `Assign to ${selectedClients.length} Client(s)`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomeworkAssignmentModal;
